import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Form, Formik } from 'formik';
import { PartnersEnum } from 'entities/Partners/enums';
import { PageHeader } from 'shared/ui/PageHeader';
import { Button } from 'shared/ui/Button';
import AdaptiveContent from 'shared/ui/adaptive-content';
import { SettingsSchemaType, settingsSchema } from './validationSchema';
import General from './partials/general';
import PartnersAssociation from './partials/partners-association';
import FtpSettings from './partials/ftp-settings';
import { AutoconnectByLabelsEnum } from 'entities/CustomModel/types';
import { GeneralModelSettings } from 'entities/CustomModel/types';

type ModelSettingsProps = {
	onSubmit: ({ id, data }: { id: string; data: GeneralModelSettings }) => void;
	modelSettings: GeneralModelSettings;
	isLoading: boolean;
	modelId: string;
	description: string;
};

const ModelSettings: FC<ModelSettingsProps> = ({
	modelSettings,
	onSubmit,
	isLoading,
	modelId,
	description,
}) => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const handleSubmit = (data: SettingsSchemaType) => {
		const autoconnectByLabels = data.settings.autoconnectByLabels?.filter(
			(item) => item,
		) as AutoconnectByLabelsEnum[];
		const payload = {
			...data,
			settings: {
				partnerType: data.settings.partnerType || undefined,
				pagesRange: data.settings.pagesRange || undefined,
				autoconnectByLabels: autoconnectByLabels.length ? autoconnectByLabels : undefined,
				sendEmailOnPartnerAssociationFailure: data.settings.sendEmailOnPartnerAssociationFailure,
				sendEmailOnItemCodeNotFound: data.settings.sendEmailOnItemCodeNotFound || undefined,
				sendEmailOnSuccessExport: data.settings.sendEmailOnSuccessExport || undefined,
				sendEmailOnReceiverIdNotFound: data.settings.sendEmailOnReceiverIdNotFound || undefined,
				splitUploadedDoc: data.settings.splitUploadedDoc,
				ftpImport: {
					autoImportInterval: data.settings.ftpImport.autoImportInterval || undefined,
					autoImportDeleteAfter: data.settings.ftpImport.autoImportDeleteAfter,
					path: data.settings.ftpImport.path || undefined,
				},
				ftpExport: {
					autoExportFormat: data.settings.ftpExport.autoExportFormat || undefined,
					path: data.settings.ftpExport.path || undefined,
					originalFilePath: data.settings.ftpExport.originalFilePath || undefined,
					compressOriginalFile: data.settings.ftpExport.compressOriginalFile,
				},
			},
		};
		onSubmit({ id: modelId, data: payload });
	};

	return (
		<>
			<PageHeader title={t('custom_model.settings.title')} description={description} />
			<AdaptiveContent>
				<Formik
					initialValues={{
						name: modelSettings?.name || '',
						settings: {
							partnerType: modelSettings?.settings?.partnerType || PartnersEnum.default,
							autoconnectByLabels: [
								...(modelSettings?.settings?.autoconnectByLabels || []),
								...Array(
									6 - (modelSettings?.settings?.autoconnectByLabels?.length || 0),
								).fill(''),
							],
							splitUploadedDoc: modelSettings?.settings?.splitUploadedDoc || false,
							sendEmailOnPartnerAssociationFailure:
								modelSettings?.settings?.sendEmailOnPartnerAssociationFailure || false,
							sendEmailOnItemCodeNotFound:
								modelSettings?.settings?.sendEmailOnItemCodeNotFound || false,
							sendEmailOnSuccessExport:
								modelSettings?.settings?.sendEmailOnSuccessExport || false,
							sendEmailOnReceiverIdNotFound:
								modelSettings?.settings?.sendEmailOnReceiverIdNotFound || false,
							ftpImport: {
								autoImportInterval:
									modelSettings?.settings?.ftpImport?.autoImportInterval ?? '',
								autoImportDeleteAfter:
									modelSettings?.settings?.ftpImport?.autoImportDeleteAfter ?? false,
								path: modelSettings?.settings?.ftpImport?.path ?? '',
							},
							ftpExport: {
								autoExportFormat: modelSettings?.settings?.ftpExport?.autoExportFormat ?? '',
								path: modelSettings?.settings?.ftpExport?.path ?? '',
								originalFilePath: modelSettings?.settings?.ftpExport?.originalFilePath ?? '',
								compressOriginalFile:
									modelSettings?.settings?.ftpExport?.compressOriginalFile ?? false,
							},
							pagesRange: modelSettings.settings?.pagesRange ?? '',
						},
					}}
					validationSchema={settingsSchema}
					onSubmit={handleSubmit}
				>
					<Form>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
							<General />

							<PartnersAssociation />

							<FtpSettings />

							<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
								<Button
									loading={isLoading}
									onClick={() => navigate(-1)}
									size={'small'}
									variant="outlined"
								>
									{t('shared_texts.cancel')}
								</Button>
								<Button loading={isLoading} size={'small'} type={'submit'}>
									{t('shared_texts.save')}
								</Button>
							</Box>
						</Box>
					</Form>
				</Formik>
			</AdaptiveContent>
		</>
	);
};

export default ModelSettings;
